import { useSearchParams } from "react-router-dom";

import {
  ContractTypeValuesFor,
  ProductTypes,
  StageValues,
} from "../constants/purchaseFlow";

// Map URL param keys to their value types
type ParamValueMap = {
  stage: StageValues;
  product: ProductTypes;
  contractType: ContractTypeValuesFor<ProductTypes>;
};

export function usePrePurchaseURLParams() {
  const [searchParams, setSearchParams] = useSearchParams();

  /**
   * Sets or deletes a URL search param.
   * @param param - The key of the URL param to set.
   * @param value - The value to set, or null to remove the param.
   */
  function setURLParam<K extends keyof ParamValueMap>(
    param: K,
    value: ParamValueMap[K] | null,
  ): void {
    const newParams = new URLSearchParams(searchParams);
    if (value === null) {
      newParams.delete(param);
    } else {
      newParams.set(param, value as string);
    }
    setSearchParams(newParams);
  }

  /**
   * Retrieves the value of a given URL search param.
   * @param param - The key of the URL param to get.
   * @returns The string value or null if not set.
   */
  function getURLParam<K extends keyof ParamValueMap>(
    param: K,
  ): ParamValueMap[K] | null {
    const value = searchParams.get(param);
    return (value as ParamValueMap[K]) ?? null;
  }

  return {
    searchParams,
    setURLParam,
    getURLParam,
  };
}
